export type AidaTypewriterSource = 'store' | 'view';

export type StartAidaTypewriterOptions = {
  streamId: string;
  chatId?: string;
  source?: AidaTypewriterSource;
  getTarget: () => string;
  isBufferClosed?: () => boolean;
  onCaughtUp?: () => void;
  startDelayMs?: number;
  msPerChar?: number;
};

type TypewriterJob = {
  streamId: string;
  chatId?: string;
  source: AidaTypewriterSource;
  getTarget: () => string;
  isBufferClosed: () => boolean;
  startDelayMs: number;
  msPerChar: number;
  visibleLength: number;
  firstTargetAt: number | null;
  revealStartedAt: number | null;
  caughtUpNotified: boolean;
  caughtUpListeners: Set<() => void>;
  listeners: Set<(visible: string) => void>;
};

export const AIDA_TYPEWRITER_START_DELAY_MS = 100;
export const AIDA_TYPEWRITER_MS_PER_CHAR = 7;
const BACKGROUND_TICK_MS = 250;

const jobs = new Map<string, TypewriterJob>();
let rafId: number | null = null;
let intervalId: number | null = null;

const isDocumentHidden = () => typeof document !== 'undefined' && document.hidden;

const emitVisible = (job: TypewriterJob, target: string) => {
  const visible = target.slice(0, job.visibleLength);
  job.listeners.forEach(listener => listener(visible));
};

const maybeNotifyCaughtUp = (job: TypewriterJob, target: string) => {
  if (job.caughtUpNotified || target.length === 0) return;
  if (job.visibleLength < target.length || !job.isBufferClosed()) return;
  job.caughtUpNotified = true;
  job.caughtUpListeners.forEach(listener => listener());
};

const alignRevealClock = (job: TypewriterJob, now: number) => {
  job.revealStartedAt = now - job.visibleLength * job.msPerChar;
};

const tickJob = (job: TypewriterJob, now: number) => {
  const target = job.getTarget();

  if (target.length > 0 && job.firstTargetAt === null) {
    job.firstTargetAt = now;
  }

  if (target.length < job.visibleLength) {
    job.visibleLength = 0;
    job.revealStartedAt = now;
    job.caughtUpNotified = false;
  }

  if (isDocumentHidden()) {
    if (job.visibleLength !== target.length) {
      job.visibleLength = target.length;
      emitVisible(job, target);
    }
    if (target.length > 0) alignRevealClock(job, now);
    maybeNotifyCaughtUp(job, target);
    return;
  }

  if (job.firstTargetAt !== null && job.revealStartedAt === null) {
    if (now - job.firstTargetAt < job.startDelayMs) return;
    job.revealStartedAt = job.firstTargetAt + job.startDelayMs;
  }

  if (job.revealStartedAt === null) return;

  const expected = Math.floor((now - job.revealStartedAt) / job.msPerChar);
  const nextLength = Math.min(target.length, Math.max(job.visibleLength, expected));

  if (nextLength !== job.visibleLength) {
    job.visibleLength = nextLength;
    emitVisible(job, target);
  }

  if (nextLength >= target.length && target.length > 0) {
    alignRevealClock(job, now);
    maybeNotifyCaughtUp(job, target);
  }
};

const tickAll = (now: number) => {
  for (const job of [...jobs.values()]) tickJob(job, now);
};

const stopSchedulers = () => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

const ensureTick = () => {
  if (jobs.size === 0) {
    stopSchedulers();
    return;
  }

  if (isDocumentHidden()) {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (intervalId === null) {
      intervalId = window.setInterval(() => {
        tickAll(performance.now());
        if (jobs.size === 0) stopSchedulers();
      }, BACKGROUND_TICK_MS);
    }
    return;
  }

  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (rafId !== null) return;

  const loop = (now: number) => {
    rafId = null;
    if (jobs.size === 0) return;
    if (isDocumentHidden()) {
      tickAll(performance.now());
      ensureTick();
      return;
    }
    tickAll(now);
    if (jobs.size > 0) rafId = requestAnimationFrame(loop);
  };

  rafId = requestAnimationFrame(loop);
};

export function pokeAidaTypewriter(): void {
  if (jobs.size === 0) return;
  tickAll(performance.now());
  ensureTick();
}

export function startAidaTypewriter(options: StartAidaTypewriterOptions): () => void {
  const source = options.source ?? 'view';
  const existing = jobs.get(options.streamId);

  if (existing) {
    if (source === 'store' || existing.source !== 'store') {
      existing.getTarget = options.getTarget;
      existing.isBufferClosed = options.isBufferClosed ?? existing.isBufferClosed;
      existing.source = source;
      existing.chatId = options.chatId ?? existing.chatId;
      existing.startDelayMs = options.startDelayMs ?? existing.startDelayMs;
      existing.msPerChar = options.msPerChar ?? existing.msPerChar;
    }
    if (options.onCaughtUp) existing.caughtUpListeners.add(options.onCaughtUp);
    ensureTick();
    return () => {
      if (options.onCaughtUp) jobs.get(options.streamId)?.caughtUpListeners.delete(options.onCaughtUp);
    };
  }

  jobs.set(options.streamId, {
    streamId: options.streamId,
    chatId: options.chatId,
    source,
    getTarget: options.getTarget,
    isBufferClosed: options.isBufferClosed ?? (() => false),
    startDelayMs: options.startDelayMs ?? AIDA_TYPEWRITER_START_DELAY_MS,
    msPerChar: options.msPerChar ?? AIDA_TYPEWRITER_MS_PER_CHAR,
    visibleLength: 0,
    firstTargetAt: null,
    revealStartedAt: null,
    caughtUpNotified: false,
    caughtUpListeners: new Set(options.onCaughtUp ? [options.onCaughtUp] : []),
    listeners: new Set(),
  });
  ensureTick();
  return () => {
    if (options.onCaughtUp) jobs.get(options.streamId)?.caughtUpListeners.delete(options.onCaughtUp);
  };
}

export function subscribeAidaTypewriter(streamId: string, listener: (visible: string) => void): () => void {
  const job = jobs.get(streamId);
  if (!job) return () => undefined;

  job.listeners.add(listener);
  listener(job.getTarget().slice(0, job.visibleLength));

  return () => {
    job.listeners.delete(listener);
  };
}

export function getAidaTypewriterVisible(streamId: string, fallbackTarget = ''): string {
  const job = jobs.get(streamId);
  if (!job) return fallbackTarget.slice(0, 0);
  return job.getTarget().slice(0, job.visibleLength);
}

export function stopAidaTypewriter(streamId: string): string {
  const job = jobs.get(streamId);
  if (!job) return '';
  const visible = job.getTarget().slice(0, job.visibleLength);
  jobs.delete(streamId);
  if (jobs.size === 0) stopSchedulers();
  return visible;
}

export function stopAidaTypewritersForChat(chatId: string): string {
  let visible = '';
  for (const job of [...jobs.values()]) {
    if (job.chatId !== chatId) continue;
    visible = stopAidaTypewriter(job.streamId) || visible;
  }
  return visible;
}

export function stopAllAidaTypewriters(): void {
  jobs.clear();
  stopSchedulers();
}

export function hasAidaTypewriter(streamId: string): boolean {
  return jobs.has(streamId);
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    pokeAidaTypewriter();
  });
}
